import { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Pencil, Plus, Trash2, FileDown } from 'lucide-react';
import {
  useConversations,
  useCreateConversation,
  useUpdateConversation,
  useDeleteConversation,
} from '../api.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Textarea, Field } from '../../../shell/Field.js';
import { useAuthStore } from '../../../store/auth.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { EmployeeSelect, formatEmployeeOption } from '../../employees/components/EmployeeSelect.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { DetailField, DetailViewModal, ViewRecordButton } from '../../../shell/DetailViewModal.js';
import { formatDate } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import type { DocumentedConversation } from '@shared/types';

function toDateIso(day: string): string {
  if (!day) return new Date().toISOString();
  return new Date(`${day}T00:00:00.000Z`).toISOString();
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function ConversationsPage() {
  const { data, isLoading } = useConversations();
  const { data: directory } = useEmployeeDirectory();
  const { user } = useAuthStore();
  const canManage = user?.role === 'Admin' || user?.role === 'Manager';
  const employeeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, formatEmployeeOption(e));
    }
    return map;
  }, [directory]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentedConversation | null>(null);
  const [viewing, setViewing] = useState<DocumentedConversation | null>(null);
  const deleteConversation = useDeleteConversation();

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await deleteConversation.mutateAsync(id);
      toast('success', 'Conversation deleted.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete.';
      toast('error', msg);
    }
  };


  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (conversation: DocumentedConversation) => {
    setEditing(conversation);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const columns: Column<DocumentedConversation>[] = [
    { key: 'date', header: 'Date', render: (c) => formatDate(c.date) },
    {
      key: 'employee',
      header: 'Employee',
      render: (c) => (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {employeeLabel.get(c.employeeId) ?? 'Employee'}
        </span>
      ),
    },
    { key: 'topic', header: 'Topic', render: (c) => <span className="font-medium">{c.topic}</span> },
    { key: 'details', header: 'Preview', render: (c) => <span className="line-clamp-1">{c.details}</span> },
    { key: 'witnesses', header: 'Witnesses', render: (c) => c.witnesses || '—' },
    {
      key: 'actions',
      header: '',
      render: (c) => (
        <div className="flex justify-end gap-1">
          <ViewRecordButton label={c.topic} onClick={() => setViewing(c)} />
          <a
            href={`/api/action-pdfs/conversation/${c.id}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <Button size="sm" variant="ghost" aria-label="Download PDF">
              <FileDown className="h-4 w-4" />
            </Button>
          </a>
          {canManage && (
            <>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Edit ${c.topic}`}
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(c);
                }}
              >
                <Pencil className="h-4 w-4" /> Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Delete ${c.topic}`}
                loading={deleteConversation.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(c.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Documented Conversations</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{data?.items.length ?? 0} conversations</p>
        </div>
        {canManage && (
          <Button onClick={openCreate}><Plus className="h-4 w-4" /> Document Conversation</Button>
        )}
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-slate-400 dark:text-slate-500">Loading…</div>
      ) : data && data.items.length > 0 ? (
        <Table columns={columns} data={data.items} />
      ) : (
        <EmptyState
          icon={<MessageSquare className="h-8 w-8" />}
          title="No conversations documented"
          description={canManage ? 'Document a conversation to get started.' : 'Conversations about you will appear here.'}
        />
      )}

      {canManage && (
        <ConversationModal open={modalOpen} onClose={closeModal} conversation={editing} />
      )}

      <ConversationViewModal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        conversation={viewing}
        employeeLabel={
          viewing ? (employeeLabel.get(viewing.employeeId) ?? 'Employee') : ''
        }
      />
    </div>
  );
}

function ConversationViewModal({
  open,
  onClose,
  conversation,
  employeeLabel,
}: {
  open: boolean;
  onClose: () => void;
  conversation: DocumentedConversation | null;
  employeeLabel: string;
}) {
  if (!conversation) return null;

  return (
    <DetailViewModal open={open} onClose={onClose} title={conversation.topic}>
      <DetailField label="Date">{formatDate(conversation.date)}</DetailField>
      <DetailField label="Employee">{employeeLabel}</DetailField>
      <DetailField label="Details">{conversation.details}</DetailField>
      {conversation.witnesses ? (
        <DetailField label="Witnesses">{conversation.witnesses}</DetailField>
      ) : null}
    </DetailViewModal>
  );
}

function ConversationModal({
  open,
  onClose,
  conversation,
}: {
  open: boolean;
  onClose: () => void;
  conversation?: DocumentedConversation | null;
}) {
  const create = useCreateConversation();


  const update = useUpdateConversation();
  const { user } = useAuthStore();
  const { data: directory } = useEmployeeDirectory();
  const employeeName = useMemo(() => {
    if (!conversation?.employeeId) return '';
    const match = directory?.items.find((e) => e.id === conversation.employeeId);
    return match ? formatEmployeeOption(match) : 'Employee';
  }, [conversation?.employeeId, directory]);
  const isEdit = Boolean(conversation);

  const [employeeId, setEmployeeId] = useState('');
  const [topic, setTopic] = useState('');
  const [details, setDetails] = useState('');
  const [witnesses, setWitnesses] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    if (!open) return;
    if (conversation) {
      setEmployeeId(conversation.employeeId);
      setTopic(conversation.topic);
      setDetails(conversation.details);
      setWitnesses(conversation.witnesses ?? '');
      setDate(toDateInput(conversation.date));
    } else {
      setEmployeeId('');
      setTopic('');
      setDetails('');
      setWitnesses('');
      setDate('');
    }
  }, [open, conversation]);

  const reset = () => {
    setEmployeeId('');
    setTopic('');
    setDetails('');
    setWitnesses('');
    setDate('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && conversation) {
        await update.mutateAsync({
          id: conversation.id,
          body: {
            topic,
            details,
            witnesses,
            date: toDateIso(date),
          },
        });
        toast('success', 'Conversation updated.');
      } else {
        await create.mutateAsync({
          employeeId, topic, details, witnesses,
          date: new Date().toISOString(),
        });
        toast('success', 'Conversation documented.');
      }
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError
        ? err.body.message
        : (isEdit ? 'Failed to update.' : 'Failed to save.');
      toast('error', msg);
    }
  };

  const pending = create.isPending || update.isPending;
  const canSubmit = isEdit
    ? Boolean(topic && details && date)
    : Boolean(employeeId && topic && details);

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? 'Edit Conversation' : 'Document Conversation'}>
      <div className="space-y-4">
        {!isEdit && (
          <EmployeeSelect
            label="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            excludeIds={user?.employeeId ? [user.employeeId] : []}
          />
        )}
        {isEdit && (
          <>
            <Field label="Employee">
              <p className="text-sm text-slate-700 dark:text-slate-300">{employeeName}</p>
            </Field>
            <Field label="Date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
          </>
        )}
        <Field label="Topic"><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Performance discussion" /></Field>
        <Textarea label="Details" value={details} onChange={(e) => setDetails(e.target.value)} rows={4} />
        <Field label="Witnesses (optional)"><Input value={witnesses} onChange={(e) => setWitnesses(e.target.value)} placeholder="Jane Doe, John Smith" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={pending} disabled={!canSubmit}>
            {isEdit ? 'Save Changes' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
